import { useQuery } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  normalizePaged,
  type ListQueryParams,
  type Paged,
} from "@/core/api/pagination";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type { SavingsMovementDto } from "@/features/savings/api/dto";

/**
 * Historique paginé des versements / retraits d'un coffre
 * (`GET /SavingsVaults/{id}/movements?page=&pageSize=`, du plus récent au
 * plus ancien) — réponse `Paged` ; `normalizePaged` tolère un tableau brut
 * transitoire. Les mouvements FAILED figurent avec `failureReason`.
 */
export const useSavingsVaultMovements = (
  vaultId: string,
  params: ListQueryParams = {},
  enabled = true
) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return useQuery({
    queryKey: QUERY_KEYS.SAVINGS_VAULT_MOVEMENTS(vaultId, {
      ...params,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const data = await fetchDataGet<
        Paged<SavingsMovementDto> | SavingsMovementDto[] | SavingsMovementDto
      >({
        url: savingsEndpoints.savingsVaults.movements(vaultId),
        queryParams: {
          ...params.filters,
          page: String(page),
          pageSize: String(pageSize),
        },
      });
      return normalizePaged(data, pageSize);
    },
    retry: (failureCount, error) =>
      !/401/.test(error?.message ?? "") && failureCount < 1,
    enabled: enabled && Boolean(vaultId),
  });
};
